import Button from "@material-ui/core/Button";
import React, { FC } from "react";
import { useDataProvider, useNotify } from "react-admin";
import { Booking } from "../../types";
import { formatDisplayDateTime, sortableDateTimeKey } from "../../utils/date";
import { IssueStatus } from "../issues";

export const getIssuesHistoryFilter = (booking: Booking) => {
  const productInstanceIds = Array.from(
    new Set(
      (booking.allocations || [])
        .map((allocation) => allocation.product_instance_id)
        .filter((id): id is number => typeof id === "number"),
    ),
  );

  const filter: Record<string, unknown> = {
    status: IssueStatus.OPEN,
  };

  if (productInstanceIds.length > 0) {
    filter.product_instance_id = productInstanceIds;
  }

  if (booking?.id != null) {
    filter.allocation_booking_id = booking.id;
  }

  return filter;
};

interface ShareIssueButtonProps {
  booking: Booking;
}

/** Matches issues list API `child_codes[]` rows (pivot + nested child_code). */
interface ChildCodeItem {
  id?: number;
  issue_id?: number;
  child_code_id?: number | string;
  quantity?: number;
  note?: string | null;
  created_at?: string;
  updated_at?: string;
  child_code?: {
    id?: number;
    product_code?: string;
    description?: string | null;
    type?: string;
    business_unit_id?: number;
    status?: string;
  };
}

/**
 * Matches issues list API issue records (embedded `product_instance`, `booking`, etc.).
 */
interface BookingIssueRecord {
  id: number | string;
  booking_id?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
  description?: string | null;
  title?: string;
  issue_type?: string;
  product_instance_id?: number;
  warehouse_note?: string | null;
  closing_remarks?: string | null;
  resolved_at?: string | null;
  resolved_by_id?: number | null;
  reporter_id?: number;
  issue_category?: {
    name?: string;
  };
  product_instance?: {
    id?: number;
    name?: string;
    status?: string;
    product_id?: number;
    product?: {
      id?: number;
      name?: string;
      type?: string;
      description?: string | null;
      business_unit_id?: number;
      status?: string;
    };
    open_issues?: Array<{ id?: number; booking_id?: number; status?: string }>;
  };
  booking?: {
    id?: number;
    date?: string;
    surgeon?: {
      id?: number;
      full_name?: string;
    };
  };
  reporter?: {
    id?: number;
    email?: string;
    status?: string;
  };
  resolved_by?: { id?: number } | null;
  child_codes?: ChildCodeItem[];
}

const getIssueTypeLabel = (issue: BookingIssueRecord): string => {
  const productType =
    issue.product_instance?.product?.type?.toLowerCase() ?? "";

  if (productType.toLowerCase() === "instrument") {
    return "faulty instrument";
  }

  if (productType.toLowerCase() === "implant") {
    return "backorder";
  }

  return issue.issue_type || issue.issue_category?.name || issue.title || "-";
};

const mapProductTypeToIssueLabel = (productType?: string): string => {
  const normalized = (productType || "").toLowerCase();
  if (normalized.toLowerCase() === "instrument") {
    return "faulty instrument";
  }
  if (normalized.toLowerCase() === "implant") {
    return "backorder";
  }
  return "";
};

const toTitleCase = (value: string): string => {
  return value
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
};

const getProductSetGroupKey = (issue: BookingIssueRecord): string => {
  const instanceId =
    issue.product_instance?.id ?? issue.product_instance_id ?? null;
  if (instanceId != null) {
    return `id:${instanceId}`;
  }
  return `name:${issue.product_instance?.name ?? "unknown"}`;
};

const sortIssuesByCreatedAtAsc = (
  a: BookingIssueRecord,
  b: BookingIssueRecord,
) => {
  // Asia/Singapore wall-time order via lexicographic key; browsers parse
  // naive "YYYY-MM-DD HH:mm:ss" differently, so plain string comparison
  // avoids new Date / localeCompare pitfalls and Invalid Date in Safari.
  const ta = sortableDateTimeKey(a.created_at);
  const tb = sortableDateTimeKey(b.created_at);
  if (ta < tb) return -1;
  if (ta > tb) return 1;
  return 0;
};

const sortIssuesByCreatedAtDesc = (
  a: BookingIssueRecord,
  b: BookingIssueRecord,
) => sortIssuesByCreatedAtAsc(b, a);

const getSurgeryDateTime = (booking: Booking): string => {
  let dateValue: string | undefined;

  if (Array.isArray(booking.dates) && booking.dates.length > 0) {
    const firstDate = booking.dates[0];
    dateValue = typeof firstDate === "string" ? firstDate : firstDate?.date;
  } else {
    dateValue = booking.date;
  }

  if (!dateValue) {
    return "-";
  }

  const formatted = formatDisplayDateTime(dateValue);
  return formatted || "-";
};

const buildHospitalLabel = (booking: Booking): string => {
  return booking.hospital?.name || "-";
};

const formatIssueShareMessage = (
  booking: Booking,
  issues: BookingIssueRecord[],
): string => {
  const headerLines = [
    `CPM ID: ${booking.id ?? "-"}`,
    `Hospital: ${buildHospitalLabel(booking)}`,
    `Surgery Date Time: ${getSurgeryDateTime(booking)}`,
    `Surgeon: ${booking.surgeon?.full_name || "-"}`,
    "",
    "Issue Details",
  ];

  const byGroup = new Map<string, BookingIssueRecord[]>();
  for (const issue of issues) {
    const key = getProductSetGroupKey(issue);
    const list = byGroup.get(key) ?? [];
    list.push(issue);
    byGroup.set(key, list);
  }

  const groupBlocks: string[][] = [];

  const sortedGroupKeys = Array.from(byGroup.keys()).sort((ka, kb) => {
    const ia = (byGroup.get(ka) ?? [])
      .slice()
      .sort(sortIssuesByCreatedAtDesc)[0];
    const ib = (byGroup.get(kb) ?? [])
      .slice()
      .sort(sortIssuesByCreatedAtDesc)[0];
    return sortIssuesByCreatedAtDesc(ia, ib);
  });

  for (const key of sortedGroupKeys) {
    const groupIssues = (byGroup.get(key) ?? [])
      .slice()
      .sort(sortIssuesByCreatedAtDesc);
    if (groupIssues.length === 0) {
      continue;
    }

    const head = groupIssues[0];
    const productType =
      head.product_instance?.product?.type?.toLowerCase() ?? "";
    const productSetName =
      productType === "other" ? "Loaner" : head.product_instance?.name || "-";
    const titleLine = productSetName;

    const lines: string[] = [titleLine, "issued item:"];

    for (const issue of groupIssues) {
      const childCodes = Array.isArray(issue.child_codes)
        ? issue.child_codes
        : [];

      const issueTypeLabel = toTitleCase(
        String(getIssueTypeLabel(issue) || "-"),
      );
      const pushLine = (
        code: string,
        description: string,
        qty: string | number,
        note: string,
        issueType: string,
      ) => {
        lines.push(
          `└─ Code: ${code} ${description} (${issueType}) | Qty: ${qty} | Note: ${note}`,
        );
      };

      if (childCodes.length === 0) {
        const desc =
          issue.description != null && String(issue.description).trim() !== ""
            ? String(issue.description).trim()
            : "-";
        pushLine("-", "-", "-", desc, issueTypeLabel);
      } else {
        for (const childCode of childCodes) {
          const mappedFromChildCodeType = mapProductTypeToIssueLabel(
            childCode?.child_code?.type,
          );
          const childCodeType = toTitleCase(
            mappedFromChildCodeType || issueTypeLabel || "-",
          );
          const childCodeDescription =
            childCode?.child_code?.description &&
            String(childCode.child_code.description).trim() !== ""
              ? String(childCode.child_code.description).trim()
              : "-";
          pushLine(
            String(
              childCode?.child_code?.product_code ||
                childCode?.child_code_id ||
                "-",
            ),
            childCodeDescription,
            childCode?.quantity ?? "-",
            childCode?.note || "-",
            childCodeType,
          );
        }
      }
    }

    groupBlocks.push(lines);
  }

  const issueLines = groupBlocks.flatMap((block, i) =>
    i < groupBlocks.length - 1 ? [...block, ""] : block,
  );

  return [...headerLines, ...issueLines].join("\n");
};

const ShareIssueButton: FC<ShareIssueButtonProps> = ({ booking }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [isSharing, setIsSharing] = React.useState(false);
  const [hasIssues, setHasIssues] = React.useState(false);
  const [isCheckingIssues, setIsCheckingIssues] = React.useState(true);

  React.useEffect(() => {
    const checkIssueAvailability = async () => {
      if (!booking?.id) {
        setHasIssues(false);
        setIsCheckingIssues(false);
        return;
      }

      try {
        const response = await dataProvider.getList("issues", {
          pagination: { page: 1, perPage: 1 },
          sort: { field: "created_at", order: "DESC" },
          filter: getIssuesHistoryFilter(booking),
        });

        setHasIssues((response?.total || 0) > 0);
      } catch (error) {
        setHasIssues(false);
      } finally {
        setIsCheckingIssues(false);
      }
    };

    checkIssueAvailability();
  }, [booking, dataProvider]);

  const handleShareIssue = async () => {
    if (isSharing) {
      return;
    }

    setIsSharing(true);

    try {
      const { data } = await dataProvider.getList("issues", {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "created_at", order: "DESC" },
        filter: getIssuesHistoryFilter(booking),
      });

      const issues: BookingIssueRecord[] = Array.isArray(data) ? data : [];

      if (issues.length === 0) {
        notify("No issue history found for this booking.", "warning");
        setHasIssues(false);
        return;
      }

      const text = formatIssueShareMessage(booking, issues);

      if (navigator.share) {
        try {
          await navigator.share({ text });
          notify("Issue history shared.", "info");
          return;
        } catch (error: any) {
          if (error?.name === "AbortError") {
            return;
          }
        }
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        notify("Issue history copied to clipboard.", "info");
      } else {
        notify(
          "Sharing is not supported in this browser. Please copy manually.",
          "warning",
        );
      }
    } catch (error: any) {
      notify(`Error: ${error?.message || "Failed to share issues"}`, "warning");
    } finally {
      setIsSharing(false);
    }
  };

  if (isCheckingIssues || !hasIssues) {
    return null;
  }

  return (
    <Button
      color="primary"
      onClick={handleShareIssue}
      disabled={isSharing}
      variant="contained"
      style={{ padding: "4px 10px", fontSize: "0.8125rem" }}
    >
      {isSharing ? "Sharing..." : "Share Issue"}
    </Button>
  );
};

export default ShareIssueButton;
